import { CampoDeFecha } from '../../../nucleo/fecha/CampoDeFecha.jsx';
import { formatearFecha } from '../../../nucleo/fecha/fechaLocal.js';
import { CampoDeImporte } from '../../../nucleo/importes/CampoDeImporte.jsx';
import { CalendarioDelPrestamo } from './CalendarioDelPrestamo.jsx';
import { importe as formatearImporte, PAGOS_MAXIMO, PAGOS_MINIMO } from '../formato.js';

/**
 * LOS SEIS DATOS DEL PLAN DE PAGOS Y SU VISTA PREVIA (RF-265; pantallas P-20-01 y
 * la generación sobre un préstamo que ya existe).
 *
 * POR QUÉ ES UNA PIEZA COMPARTIDA: porque el plan se declara en DOS momentos —al dar
 * de alta el préstamo (P-20-01) y después, si el alta se quedó sin plan (P-20-06,
 * D-457)— y los dos piden EXACTAMENTE lo mismo. Con dos formularios parecidos, la
 * próxima regla del plazo se aplica en uno y se olvida en el otro.
 *
 * LA VISTA PREVIA LA CALCULA EL SERVIDOR con la MISMA preparación con la que va a
 * guardar (§2, patrón D-78/D-413): esta pantalla no reparte importes, no cuenta
 * pagos y no calcula ninguna fecha (RNF-038, RNF-138). Si la vista previa y lo
 * guardado dieran cifras distintas, sería defecto.
 *
 * AQUÍ NO SE CAPTURA NINGÚN GASTO Y NO SE CREA NINGÚN MOVIMIENTO (RF-265, CA-20-06):
 * la deuda ya la puso el alta de la cuenta (D-431) y lo ya pagado antes de la
 * aplicación no se vuelve a contar (D-433).
 */
export function FormularioDelPlan({
  valores,
  alCambiar,
  codigoISO,
  categorias,
  vistaPrevia,
  hoy,
  deshabilitarMontoOriginal = false,
}) {
  const cambiar = (campo) => (evento) => alCambiar(campo, evento.target.value);
  const cambiarValor = (campo) => (valor) => alCambiar(campo, valor);

  return (
    <>
      <div className="card" style={{ margin: 12 }} data-prueba="prestamo-plan-datos">
        <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>SU PLAN DE PAGOS</h4>

        <div className="campo">
          <label htmlFor="montoOriginal">MONTO ORIGINAL DEL PRÉSTAMO</label>
          {/* RNF-133, heredada tal cual: el importe conserva su valor y queda
              SELECCIONADO COMPLETO al tomar el foco. Se usa el CAMPO ÚNICO del
              núcleo sin pasarle ninguna propiedad nueva —el componente sigue
              congelado (DEUDA-19-01 sin autorización)— y jamás se deshabilita. */}
          <CampoDeImporte
            id="montoOriginal"
            valor={valores.montoOriginal}
            alCambiar={cambiarValor('montoOriginal')}
            codigoDivisa={codigoISO}
            contexto="el monto original del préstamo"
            data-prueba="prestamo-campo-monto-original"
          />
          <span className="ayuda">
            {deshabilitarMontoOriginal
              ? 'Es cuánto te prestaron al principio, no lo que debes hoy.'
              : 'Cuánto te prestaron al principio. Lo que debes hoy se captura arriba.'}
          </span>
        </div>

        <div className="campo">
          <label htmlFor="pagoMensual">PAGO MENSUAL DEL CONTRATO</label>
          <CampoDeImporte
            id="pagoMensual"
            valor={valores.pagoMensual}
            alCambiar={cambiarValor('pagoMensual')}
            codigoDivisa={codigoISO}
            contexto="el pago mensual del préstamo"
            data-prueba="prestamo-campo-pago-mensual"
          />
          {/* El literal lo arma el servidor cuando hay vista previa (RNF-130); sin
              ella se usa el mismo texto de partida, que es el suyo. */}
          <span className="ayuda" data-prueba="prestamo-ayuda-pago-mensual">
            {vistaPrevia?.ayudaDelPagoMensual
              ?? 'Es una proyección: el interés real de cada mes lo registras al pagar.'}
          </span>
        </div>

        <div className="campo">
          <label htmlFor="numeroDePagos">PLAZO, EN PAGOS</label>
          <input
            id="numeroDePagos"
            name="numeroDePagos"
            type="number"
            min={PAGOS_MINIMO}
            max={PAGOS_MAXIMO}
            value={valores.numeroDePagos}
            onChange={cambiar('numeroDePagos')}
            data-prueba="prestamo-campo-plazo"
          />
          <span className="ayuda">
            De {PAGOS_MINIMO} a {PAGOS_MAXIMO}. Una hipoteca de 30 años cabe completa: son 360 pagos.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="pagosRealizados">PAGOS QUE YA HICISTE</label>
          <input
            id="pagosRealizados"
            name="pagosRealizados"
            type="number"
            min={0}
            value={valores.pagosRealizados}
            onChange={cambiar('pagosRealizados')}
            data-prueba="prestamo-campo-pagos-hechos"
          />
          <span className="ayuda">
            Si empiezas hoy, pon 0. Los pagos que ya hiciste no se vuelven a contar en ningún mes.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="fechaDelProximoPago">FECHA DEL PRÓXIMO PAGO</label>
          <CampoDeFecha
            id="fechaDelProximoPago"
            name="fechaDelProximoPago"
            valor={valores.fechaDelProximoPago}
            alCambiar={cambiarValor('fechaDelProximoPago')}
            min={hoy}
            datoDePrueba="prestamo-campo-fecha-proximo-pago"
          />
          <span className="ayuda">
            La que trae tu estado de cuenta. De ella sale el día de pago de todos los meses.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="idCategoriaDelInteres">¿EN QUÉ CATEGORÍA VA EL INTERÉS QUE PAGUES?</label>
          <select
            id="idCategoriaDelInteres"
            name="idCategoriaDelInteres"
            value={valores.idCategoriaDelInteres}
            onChange={cambiar('idCategoriaDelInteres')}
            data-prueba="prestamo-campo-categoria"
          >
            <option value="">Elige la categoría…</option>
            {categorias.map((categoria) => (
              <option key={categoria.idCategoria} value={categoria.idCategoria}>
                {categoria.nombre}
              </option>
            ))}
          </select>
          <span className="ayuda">Puedes cambiarla en cada pago, si ese mes va a otra parte.</span>
        </div>
      </div>

      {/* ===== LA VISTA PREVIA (RF-265) =====================================
          Es de SOLO LECTURA y NO GUARDA NADA: cero filas, cero bitácora
          (CA-20-08). Se recalcula con cada cambio. */}
      {vistaPrevia && (
        <div className="card" style={{ margin: 12 }} data-prueba="prestamo-vista-previa">
          <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>ASÍ VA A QUEDAR</h4>

          <div className="fila">
            <span className="crece">Se registra como</span>
            <b data-prueba="prestamo-previa-identificacion">{vistaPrevia.textoIdentificacion}</b>
          </div>
          <div className="fila">
            <span className="crece">Empieza en el pago</span>
            {/* El contador viaja armado (RNF-038): es el avance con el que NACE. */}
            <b data-prueba="prestamo-previa-contador">{vistaPrevia.contadorDeAvance}</b>
          </div>
          <div className="fila">
            <span className="crece">Debes hoy</span>
            <b data-prueba="prestamo-previa-deuda">{formatearImporte(vistaPrevia.deudaDeHoy, codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Ya le bajaste</span>
            <b>{formatearImporte(vistaPrevia.capitalYaBajado, codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">Has entregado en tus pagos anteriores</span>
            <b>{formatearImporte(vistaPrevia.entregadoEstimado, codigoISO)}</b>
          </div>
          {vistaPrevia.contadorDelSiguientePago && (
            <div className="fila">
              <span className="crece">El que sigue</span>
              <b data-prueba="prestamo-previa-siguiente">
                {vistaPrevia.contadorDelSiguientePago}
                {' · '}
                {formatearFecha(vistaPrevia.fechaDelSiguientePago)}
              </b>
            </div>
          )}
          <div className="fila">
            <span className="crece">Te faltan</span>
            <b data-prueba="prestamo-previa-faltan">
              {vistaPrevia.pagosQueFaltan} pagos
              {vistaPrevia.fechaDelUltimoPago && ` · hasta el ${formatearFecha(vistaPrevia.fechaDelUltimoPago)}`}
            </b>
          </div>
          <div className="fila">
            <span className="crece">Por entregar</span>
            <b>{formatearImporte(vistaPrevia.totalPorEntregar, codigoISO)}</b>
          </div>
          <div className="fila">
            <span className="crece">De eso, interés futuro estimado</span>
            <b data-prueba="prestamo-previa-interes-futuro">
              {formatearImporte(vistaPrevia.interesFuturoEstimado, codigoISO)}
            </b>
          </div>
          <span className="ayuda">
            El interés futuro es una proyección de tu contrato: el reparto exacto de cada mes lo
            escribes tú al pagar.
          </span>

          {/* Los avisos los REDACTA EL SERVIDOR (RNF-130) y NINGUNO BLOQUEA. */}
          {vistaPrevia.textoAvisoDeSaldoOmitido && (
            <div className="aviso w" role="status" data-prueba="prestamo-previa-aviso-saldo">
              ⚠ {vistaPrevia.textoAvisoDeSaldoOmitido}
            </div>
          )}
          {vistaPrevia.textoAvisoDeFinDeMes && (
            <div className="aviso i" role="status" data-prueba="prestamo-previa-aviso-fin-de-mes">
              {vistaPrevia.textoAvisoDeFinDeMes}
            </div>
          )}
          {vistaPrevia.sePlegaraPorAnio && (
            <div className="aviso i" role="status" data-prueba="prestamo-previa-aviso-pliegue">
              Son más de 24 pagos: en el préstamo los vas a ver agrupados por año, con el año del que
              sigue abierto.
            </div>
          )}

          <div className="ayuda" data-prueba="prestamo-previa-pagos-anteriores">
            {vistaPrevia.textoDeLosPagosAnteriores}
          </div>

          {/* El calendario QUE VA A NACER, con el mismo pliegue por año del detalle
              (RN-776). En la vista previa todo está por caer, así que el estado
              agregado del año no se enseña: un renglón que dice siempre lo mismo
              deja de leerse. */}
          <CalendarioDelPrestamo
            mensualidades={vistaPrevia.mensualidades}
            codigoISO={codigoISO}
            datoDePrueba="prestamo-previa-calendario"
            mostrarEstadoAgregado={false}
          />
        </div>
      )}

      <div className="card" style={{ margin: 12 }}>
        <h4 style={{ margin: '0 0 6px', fontSize: 11.5 }}>ESTO NO REGISTRA NINGÚN MOVIMIENTO</h4>
        <span className="ayuda" data-prueba="prestamo-no-es-un-gasto">
          Aquí no se captura ningún gasto y no se crea ningún movimiento: lo que debes es lo que
          declaraste, y generar el plan no la mueve ni un centavo. <b>Lo que ya pagaste no se vuelve a
          contar en ningún mes.</b> De tu siguiente pago en adelante, cada uno queda registrado.
        </span>
      </div>
    </>
  );
}
